// src/pages/Accueil.jsx
import React, { useState, useEffect } from 'react';
import { Row, Col, Spinner, Alert, Button, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import { COLORS } from '../constants/colors';

export default function Accueil() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    user: {},
    eglise: {},
    bienvenue: {},
    stats: { membres: 0, evenements: 0 },
    prochain_evenement: null
  });

  useEffect(() => {
    fetchAccueilData();
  }, []);

  const fetchAccueilData = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        navigate('/login');
        return;
      }

      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;

      const response = await fetch(`https://josephsbusiness.com/api/accueil.php?user_id=${userId}`, {
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) throw new Error('Erreur chargement');

      const result = await response.json();

      if (result.success) {
        setData(result.data);
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return format(new Date(dateString), 'dd MMMM yyyy', { locale: fr });
  };

  const parseCultes = (culteText) => {
    if (!culteText) return [];
    return culteText.split('<br>').map(line => {
      const clean = line.replace(/<[^>]*>/g, '').trim();
      if (!clean) return null;

      const match = clean.match(/([A-Z]+)\s+(\d+H\d+)\s*:\s*(.+)/);
      if (match) {
        return {
          jour: match[1],
          heure: match[2],
          titre: match[3]
        };
      }
      return { titre: clean };
    }).filter(Boolean);
  };

  const cultes = parseCultes(data.bienvenue.culte);

  const actionsLines = data.bienvenue.action
    ? data.bienvenue.action.split(/<br\s*\/?>/i)
        .map(line => line.replace(/<[^>]+>/g, '').trim())
        .filter(line => line)
    : [];

  if (loading) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
            <div className="text-center">
              <Spinner animation="border" variant="primary" style={{ width: '4rem', height: '4rem' }} />
              <h5 className="fw-bold mt-3" style={{ color: COLORS.primaryDark }}>Chargement de l'accueil</h5>
              <p className="text-muted">Récupération des données en cours...</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
          <Alert variant="danger" className="border-0 shadow-sm rounded-3">
            <div className="d-flex align-items-center">
              <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
              <div>
                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                <p className="mb-0 small">{error}</p>
                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchAccueilData}>
                  <i className="ri-refresh-line me-1"></i>Réessayer
                </Button>
              </div>
            </div>
          </Alert>
        </div>
        <Footer />
      </>
    );
  }

  const isSuperAdmin = data.user.is_super_admin;

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}>
        <div className="container-fluid px-0 px-lg-3">

          {/* ===== EN‑TÊTE MODERNE ===== */}
          <div className="header-modern mb-4 fade-in">
            <div className="header-left">
              <div className="header-icon-wrapper" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.indigo})` }}>
                <i className="ri-home-4-line"></i>
              </div>
              <div>
                <h5 className="gradient-text">Accueil</h5>
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge-modern">
                    <i className="ri-group-line"></i> {data.stats.membres} membres
                  </span>
                  <span className="badge-modern" style={{ background: `${COLORS.success}20`, color: COLORS.success }}>
                    <i className="ri-calendar-line"></i> {data.stats.evenements} événements
                  </span>
                  {!isSuperAdmin && data.eglise.nom && (
                    <span className="badge-modern" style={{ background: `${COLORS.purple}20`, color: COLORS.purple }}>
                      <i className="ri-church-line"></i> {data.eglise.nom}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <button className="btn-modern-outline" onClick={fetchAccueilData} disabled={loading}>
                <i className={`ri-refresh-line ${loading ? 'fa-spin' : ''}`}></i> Actualiser
              </button>
            </div>
          </div>

          {/* ===== CARTE UTILISATEUR ===== */}
          <Card className="user-card-modern mb-4 fade-in" style={{
            border: 'none',
            borderRadius: '0px',
            background: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
            overflow: 'hidden'
          }}>
            <Card.Body className="p-4">
              <div className="d-flex flex-column flex-md-row align-items-center gap-4">
                <div className="user-avatar" style={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '4px',
                  background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.indigo})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '2rem',
                  fontWeight: '700',
                  boxShadow: '0 8px 20px rgba(41,90,167,0.2)',
                  flexShrink: 0
                }}>
                  {data.user.prenom?.charAt(0)}{data.user.nom?.charAt(0)}
                </div>
                <div className="flex-grow-1 text-center text-md-start">
                  <h4 className="fw-bold mb-1" style={{ color: COLORS.primaryDark }}>{data.user.prenom} {data.user.nom}</h4>
                  <p className="text-muted small mb-0">{data.user.role}</p>
                </div>
                <div className="user-role-badge" style={{
                  background: `${COLORS.secondary}20`,
                  color: COLORS.secondary,
                  padding: '0.4rem 1.2rem',
                  borderRadius: '30px',
                  fontWeight: '600',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <i className="ri-user-line"></i>
                  <span>{data.user.role}</span>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* ===== STATISTIQUES ===== */}
          <Row className="g-3 mb-4 fade-in">
            <Col md={6}>
              <div className="kpi-card kpi-primary">
                <div className="kpi-icon"><i className="ri-group-line fs-24"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Membres</span>
                  <h3 className="kpi-value">{data.stats.membres}</h3>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="kpi-card" style={{ borderLeftColor: COLORS.success }}>
                <div className="kpi-icon" style={{ background: 'rgba(16,185,129,0.1)', color: COLORS.success }}>
                  <i className="ri-calendar-line fs-24"></i>
                </div>
                <div className="kpi-content">
                  <span className="kpi-label">Événements</span>
                  <h3 className="kpi-value" style={{ color: COLORS.success }}>{data.stats.evenements}</h3>
                </div>
              </div>
            </Col>
          </Row>

          {/* ===== VISION & MISSION ===== */}
          <Row className="g-3 mb-4">
            <Col md={6}>
              <Card className="info-card-modern fade-in" style={{
                border: 'none',
                borderRadius: '0px',
                background: 'rgba(255,255,255,0.85)',
                backdropFilter: 'blur(8px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                height: '100%'
              }}>
                <Card.Body>
                  <div className="card-icon-wrapper mb-3" style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '4px',
                    background: `${COLORS.primary}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <i className="ri-eye-line" style={{ color: COLORS.primary, fontSize: '1.5rem' }}></i>
                  </div>
                  <h6 className="fw-semibold mb-2" style={{ color: COLORS.primary, fontSize: '1.1rem' }}>Notre Vision</h6>
                  <p className="text-muted" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>{data.bienvenue.vision || 'Aucune information disponible'}</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="info-card-modern fade-in" style={{
                border: 'none',
                borderRadius: '0px',
                background: 'rgba(255,255,255,0.85)',
                backdropFilter: 'blur(8px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                height: '100%'
              }}>
                <Card.Body>
                  <div className="card-icon-wrapper mb-3" style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '4px',
                    background: `${COLORS.success}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <i className="ri-flag-line" style={{ color: COLORS.success, fontSize: '1.5rem' }}></i>
                  </div>
                  <h6 className="fw-semibold mb-2" style={{ color: COLORS.success, fontSize: '1.1rem' }}>Notre Mission</h6>
                  {data.bienvenue.mission ? (
                    <div className="text-muted" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
                      <i className="ri-double-quotes-l" style={{ color: COLORS.secondary, fontSize: '1.2rem', opacity: 0.5, marginRight: '4px' }}></i>
                      <span dangerouslySetInnerHTML={{ __html: data.bienvenue.mission.replace(/<br>/g, '<br/>') }} />
                      <i className="ri-double-quotes-r" style={{ color: COLORS.secondary, fontSize: '1.2rem', opacity: 0.5, marginLeft: '4px' }}></i>
                    </div>
                  ) : (
                    <p className="text-muted" style={{ fontSize: '0.95rem' }}>Aucune information disponible</p>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ===== CULTES & ACTIONS ===== */}
          <Row className="g-3 mb-4">
            <Col md={6}>
              <Card className="info-card-modern fade-in" style={{
                border: 'none',
                borderRadius: '0px',
                background: 'rgba(255,255,255,0.85)',
                backdropFilter: 'blur(8px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                height: '100%'
              }}>
                <Card.Body>
                  <div className="card-icon-wrapper mb-3" style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '4px',
                    background: `${COLORS.purple}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <i className="ri-calendar-event-line" style={{ color: COLORS.purple, fontSize: '1.5rem' }}></i>
                  </div>
                  <h6 className="fw-semibold mb-3" style={{ color: COLORS.purple, fontSize: '1.1rem' }}>Nos Cultes</h6>
                  <div className="cultes-list">
                    {cultes.map((culte, index) => (
                      <div key={index} className="culte-item" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '0.5rem 0',
                        borderBottom: '1px solid rgba(0,0,0,0.04)'
                      }}>
                        {culte.heure ? (
                          <>
                            <span className="culte-badge" style={{
                              background: `${COLORS.primary}15`,
                              color: COLORS.primary,
                              padding: '0.2rem 0.8rem',
                              borderRadius: '30px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              whiteSpace: 'nowrap'
                            }}>{culte.jour} {culte.heure}</span>
                            <span style={{ fontSize: '0.9rem', color: COLORS.primaryDark }}>{culte.titre}</span>
                          </>
                        ) : (
                          <span style={{ fontSize: '0.9rem', color: COLORS.primaryDark }}>{culte.titre}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="info-card-modern fade-in" style={{
                border: 'none',
                borderRadius: '0px',
                background: 'rgba(255,255,255,0.85)',
                backdropFilter: 'blur(8px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                height: '100%'
              }}>
                <Card.Body>
                  <div className="card-icon-wrapper mb-3" style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '4px',
                    background: `${COLORS.secondary}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <i className="ri-hand-heart-line" style={{ color: COLORS.secondary, fontSize: '1.5rem' }}></i>
                  </div>
                  <h6 className="fw-semibold mb-3" style={{ color: COLORS.secondary, fontSize: '1.1rem' }}>Nos Actions</h6>
                  <div className="actions-list" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {actionsLines.map((action, index) => (
                      <div key={index} className="action-chip" style={{
                        background: `${COLORS.secondary}10`,
                        padding: '0.5rem 1rem',
                        borderRadius: '30px',
                        color: COLORS.secondary,
                        fontSize: '0.85rem',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <i className="ri-heart-line" style={{ fontSize: '0.9rem' }}></i>
                        <span>{action}</span>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ===== PROCHAIN ÉVÉNEMENT ===== */}
          {data.prochain_evenement && (
            <Card className="info-card-modern mb-4 fade-in" style={{
              border: 'none',
              borderRadius: '0px',
              background: 'rgba(255,255,255,0.85)',
              backdropFilter: 'blur(8px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
            }}>
              <Card.Body className="p-3">
                <div className="d-flex align-items-center gap-3">
                  <div className="event-icon" style={{
                    width: '45px',
                    height: '45px',
                    borderRadius: '4px',
                    background: `${COLORS.secondary}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <i className="ri-calendar-line" style={{ color: COLORS.secondary, fontSize: '1.3rem' }}></i>
                  </div>
                  <div className="flex-grow-1">
                    <small className="text-muted d-block" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Prochain événement</small>
                    <h6 className="fw-bold mb-0" style={{ color: COLORS.primaryDark }}>{data.prochain_evenement.titre}</h6>
                    <span className="small" style={{ color: COLORS.primary, fontWeight: '600' }}>{formatDate(data.prochain_evenement.date)}</span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* ===== VERSET BIBLIQUE ===== */}
          <Card className="verse-card-modern mb-4 fade-in" style={{
            border: 'none',
            borderRadius: '0px',
            background: `linear-gradient(135deg, ${COLORS.primaryDark}, ${COLORS.primary})`,
            boxShadow: '0 10px 30px rgba(41,90,167,0.2)',
            overflow: 'hidden',
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              top: '-20px',
              left: '20px',
              fontSize: '8rem',
              color: 'rgba(255,255,255,0.05)',
              fontFamily: 'serif'
            }}>“</div>
            <div style={{
              position: 'absolute',
              bottom: '-60px',
              right: '20px',
              fontSize: '8rem',
              color: 'rgba(255,255,255,0.05)',
              fontFamily: 'serif'
            }}>”</div>
            <Card.Body className="p-4 text-center" style={{ position: 'relative', zIndex: 2 }}>
              <p className="fw-light mb-3" style={{
                color: 'white',
                fontSize: '1.4rem',
                fontStyle: 'italic',
                lineHeight: '1.6'
              }}>
                « Car là où deux ou trois sont assemblés en mon nom, je suis au milieu d'eux. »
              </p>
              <p className="mb-0" style={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: '0.9rem',
                letterSpacing: '1px'
              }}>
                {data.bienvenue.reference} {data.bienvenue.chapitre && `- ${data.bienvenue.chapitre}`}
              </p>
            </Card.Body>
          </Card>

          {/* ===== CONTACT ===== */}
          {!isSuperAdmin && data.eglise && (data.eglise.adresse || data.eglise.telephone || data.eglise.email) && (
            <Card className="contact-card-modern fade-in" style={{
              border: 'none',
              borderRadius: '0px',
              background: 'rgba(255,255,255,0.85)',
              backdropFilter: 'blur(8px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
            }}>
              <Card.Body className="p-3">
                <Row className="g-3">
                  {data.eglise.adresse && (
                    <Col md={4}>
                      <div className="d-flex align-items-center gap-3">
                        <div className="contact-icon" style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '4px',
                          background: `${COLORS.primary}20`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <i className="ri-map-pin-line" style={{ color: COLORS.primary, fontSize: '1.1rem' }}></i>
                        </div>
                        <div>
                          <small className="text-muted d-block" style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Adresse</small>
                          <span style={{ fontSize: '0.85rem', fontWeight: '500', color: COLORS.primaryDark }}>{data.eglise.adresse}{data.eglise.ville ? `, ${data.eglise.ville}` : ''}</span>
                        </div>
                      </div>
                    </Col>
                  )}
                  {data.eglise.telephone && (
                    <Col md={4}>
                      <div className="d-flex align-items-center gap-3">
                        <div className="contact-icon" style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '4px',
                          background: `${COLORS.success}20`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <i className="ri-phone-line" style={{ color: COLORS.success, fontSize: '1.1rem' }}></i>
                        </div>
                        <div>
                          <small className="text-muted d-block" style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Téléphone</small>
                          <span style={{ fontSize: '0.85rem', fontWeight: '500', color: COLORS.primaryDark }}>{data.eglise.telephone}</span>
                        </div>
                      </div>
                    </Col>
                  )}
                  {data.eglise.email && (
                    <Col md={4}>
                      <div className="d-flex align-items-center gap-3">
                        <div className="contact-icon" style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '4px',
                          background: `${COLORS.purple}20`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <i className="ri-mail-line" style={{ color: COLORS.purple, fontSize: '1.1rem' }}></i>
                        </div>
                        <div>
                          <small className="text-muted d-block" style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Email</small>
                          <span style={{ fontSize: '0.85rem', fontWeight: '500', color: COLORS.primaryDark }}>{data.eglise.email}</span>
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
              </Card.Body>
            </Card>
          )}

          <Footer />
        </div>
      </div>

      {/* ===== STYLES ===== */}
      <style>{`
        .fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .header-modern {
          background: white;
          padding: 1.2rem 1.8rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
          border-left: 4px solid ${COLORS.secondary};
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
        }
        .header-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .header-icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.4rem;
        }
        .gradient-text {
          color: #1e293b;
          font-size: 1.3rem;
          font-weight: 700;
          margin: 0;
        }
        .badge-modern {
          background: #f1f4f9;
          color: #475569;
          padding: 0.3rem 0.8rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .badge-modern i {
          color: ${COLORS.secondary};
        }
        .btn-modern-outline {
          border: 1px solid #d1d5db;
          background: white;
          padding: 0.4rem 1.2rem;
          border-radius: 4px;
          font-size: 0.85rem;
          font-weight: 500;
          color: #4b5563;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .btn-modern-outline:hover {
          border-color: ${COLORS.secondary};
          color: ${COLORS.secondary};
          background: #fafafa;
        }

        .kpi-card {
          background: white;
          border-radius: 0px;
          padding: 1rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          transition: all 0.2s ease;
          height: 100%;
          border-left: 2px solid;
        }
        .kpi-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.08);
        }
        .kpi-primary { border-left-color: ${COLORS.primary}; }
        .kpi-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .kpi-primary .kpi-icon { background: rgba(41, 90, 167, 0.1); color: ${COLORS.primary}; }
        .kpi-content { flex: 1; }
        .kpi-label {
          font-size: 0.7rem;
          text-transform: uppercase;
          color: #6c757d;
          font-weight: 600;
          letter-spacing: 0.5px;
        }
        .kpi-value {
          font-size: 1.3rem;
          font-weight: 700;
          margin: 0.25rem 0;
          color: #1a3c5e;
        }
        .fs-24 { font-size: 1.5rem; }

        .info-card-modern {
          transition: all 0.3s ease;
        }
        .info-card-modern:hover {
          box-shadow: 0 8px 20px rgba(0,0,0,0.06) !important;
        }

        .user-card-modern {
          transition: all 0.3s ease;
        }
        .user-card-modern:hover {
          box-shadow: 0 16px 40px rgba(0,0,0,0.08) !important;
        }

        .verse-card-modern {
          transition: all 0.3s ease;
        }
        .verse-card-modern:hover {
          transform: translateY(-3px);
          box-shadow: 0 16px 40px rgba(41,90,167,0.25) !important;
        }

        .contact-card-modern {
          transition: all 0.3s ease;
        }
        .contact-card-modern:hover {
          box-shadow: 0 8px 20px rgba(0,0,0,0.06) !important;
        }

        .culte-item:last-child {
          border-bottom: none !important;
        }

        @media (max-width: 768px) {
          .header-modern {
            flex-direction: column;
            align-items: stretch;
            gap: 0.8rem;
            padding: 1rem;
          }
          .header-left {
            flex-wrap: wrap;
          }
          .user-card-modern .d-flex {
            flex-direction: column !important;
            text-align: center;
          }
        }
      `}</style>
    </>
  );
}